import type mapboxgl from 'mapbox-gl';

/* ============================================================
   Map snapshot — captures the live Mapbox canvas and renders a
   branded "Empire Maps" caption bar in a separate band *below*
   the map (nothing is drawn over the map except team logos).
   Requires the map to be created with preserveDrawingBuffer: true.
   ============================================================ */

export interface SnapshotCardData {
    /** Big title — usually the league name, e.g. "NCAA Basketball". */
    title: string;
    /** Smaller line under the title — e.g. "As of Mar 31, 2024". */
    subtitle?: string;
    /** Optional pill shown next to the brand, e.g. "HERO". */
    badge?: string;
    /** Small uppercase label above the leader name, e.g. "Current Leader". */
    leaderLabel?: string;
    /** Leader / champion name. */
    leaderName?: string;
    /** Hex color used for the leader swatch. */
    leaderColor?: string;
    /** Stat line, e.g. "1,234 counties · 42%". */
    leaderMeta?: string;
    /** Site URL shown next to the brand. Defaults to "empiremaps.live". */
    siteUrl?: string;
}

/** A logo to re-draw onto the map (the only thing allowed over the map). */
export interface SnapshotLogo {
    lng: number;
    lat: number;
    /** Rendered size in CSS pixels. */
    size: number;
    src: string;
    /** Fantasy owner avatars render as circles with a colored ring. */
    circular?: boolean;
    ringColor?: string;
    /** Initial shown when the avatar image cannot be loaded. */
    fallbackInitial?: string;
}

const BRAND_ORANGE = '#f97316';
const TEXT_PRIMARY = '#f5f5f5';
const TEXT_SECONDARY = '#9ca3af';
const BAND_BG = '#0d0d0d';

const SNAPSHOT_IMAGE_PROXY = `${process.env.REACT_APP_API_URL || '/api/v2'}/assets/image`;

/** Host suffixes allowed through the snapshot image proxy. */
const PROXY_HOST_SUFFIXES = [
    'sleepercdn.com',
    'amazonaws.com',
    'yimg.com',
    'yahoo.com',
    'myfantasyleague.com',
    'fleaflicker.com',
    'cloudfront.net',
];

function isProxyableHost(hostname: string): boolean {
    const host = hostname.toLowerCase();
    return PROXY_HOST_SUFFIXES.some((suffix) => host === suffix || host.endsWith(`.${suffix}`));
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
    const radius = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + w, y, x + w, y + h, radius);
    ctx.arcTo(x + w, y + h, x, y + h, radius);
    ctx.arcTo(x, y + h, x, y, radius);
    ctx.arcTo(x, y, x + w, y, radius);
    ctx.closePath();
}

/** Draws a simple Empire Maps shield, filled orange, in an s×s box at (x, y). */
function drawShield(ctx: CanvasRenderingContext2D, x: number, y: number, s: number) {
    ctx.save();
    ctx.fillStyle = BRAND_ORANGE;
    ctx.beginPath();
    ctx.moveTo(x + s * 0.5, y);
    ctx.lineTo(x + s, y + s * 0.18);
    ctx.lineTo(x + s, y + s * 0.55);
    ctx.quadraticCurveTo(x + s, y + s * 0.86, x + s * 0.5, y + s);
    ctx.quadraticCurveTo(x, y + s * 0.86, x, y + s * 0.55);
    ctx.lineTo(x, y + s * 0.18);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = BAND_BG;
    ctx.lineWidth = s * 0.1;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(x + s * 0.32, y + s * 0.5);
    ctx.lineTo(x + s * 0.45, y + s * 0.63);
    ctx.lineTo(x + s * 0.7, y + s * 0.34);
    ctx.stroke();
    ctx.restore();
}

export function waitForMapIdle(
    map: mapboxgl.Map,
    opts: { timeoutMs?: number } = {},
): Promise<void> {
    const timeoutMs = opts.timeoutMs ?? 600;
    return new Promise((resolve) => {
        let done = false;
        const finish = () => {
            if (done) return;
            done = true;
            resolve();
        };
        // Already painted and quiet — resolve on next frame.
        if (!map.isMoving() && !map.isZooming() && !map.isRotating()) {
            map.triggerRepaint();
            // Still wait for a real idle when timeout is long (homepage stills).
            if (timeoutMs <= 600) {
                requestAnimationFrame(() => finish());
            }
        }
        map.once('idle', finish);
        map.triggerRepaint();
        setTimeout(finish, timeoutMs);
    });
}

/** Reject blank / near-empty Mapbox captures (tiles not painted yet). */
export function mapCanvasLooksPainted(map: mapboxgl.Map): boolean {
    try {
        const canvas = map.getCanvas();
        if (canvas.width < 32 || canvas.height < 32) return false;
        // Requires map created with preserveDrawingBuffer: true.
        // A painted continental US choropleth data-URL is typically >> 40KB.
        const url = canvas.toDataURL('image/png');
        return url.length >= 40_000;
    } catch {
        return true;
    }
}

function isSameOrigin(src: string): boolean {
    try {
        return new URL(src, window.location.href).origin === window.location.origin;
    } catch {
        return false;
    }
}

/** Resolve a marker image URL to something the snapshot canvas can draw. */
function snapshotImageUrl(src: string): string {
    if (!src) return src;
    try {
        const u = new URL(src, window.location.href);
        if (u.origin === window.location.origin) return u.href;
        if (u.protocol === 'https:' && isProxyableHost(u.hostname)) {
            return `${SNAPSHOT_IMAGE_PROXY}?url=${encodeURIComponent(u.href)}`;
        }
    } catch {
        /* ignore */
    }
    return src;
}

function loadSnapshotImage(src: string): Promise<HTMLImageElement | null> {
    const url = snapshotImageUrl(src);
    return new Promise((resolve) => {
        const img = new Image();
        if (!isSameOrigin(url)) img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = url;
    });
}

function drawSnapshotFallbackAvatar(
    ctx: CanvasRenderingContext2D,
    logo: SnapshotLogo,
    cx: number,
    cy: number,
    s: number,
) {
    const r = s / 2;
    if (logo.ringColor) {
        ctx.fillStyle = logo.ringColor;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fill();
    }
    ctx.fillStyle = '#1e293b';
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.82, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#f8fafc';
    ctx.font = `800 ${Math.max(10, Math.round(s * 0.4))}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText((logo.fallbackInitial || '?').slice(0, 1).toUpperCase(), cx, cy + s * 0.02);
}

function drawSnapshotLogo(
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    logo: SnapshotLogo,
    cx: number,
    cy: number,
    s: number,
    dpr: number,
) {
    if (logo.circular) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, s / 2, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(img, cx - s / 2, cy - s / 2, s, s);
        ctx.restore();
        if (logo.ringColor) {
            ctx.strokeStyle = logo.ringColor;
            ctx.lineWidth = Math.max(2, 3 * dpr);
            ctx.beginPath();
            ctx.arc(cx, cy, s / 2, 0, Math.PI * 2);
            ctx.stroke();
        }
        return;
    }
    ctx.drawImage(img, cx - s / 2, cy - s / 2, s, s);
}

/** Fixed homepage / OG still size @2x — desktop panel is ~1100 CSS px wide. */
export const HOME_MAP_STILL_W = 2400;
export const HOME_MAP_STILL_H = 1502;

export type CaptureMapSnapshotOptions = {
    /**
     * When true, export only the map + logos (no caption band).
     * Used for homepage / OG stills that already have their own chrome.
     * Output is framed to HOME_MAP_STILL_W × HOME_MAP_STILL_H.
     */
    mapOnly?: boolean;
    /**
     * Seed/admin only: temporarily switch to mercator + fit US bounds.
     * Never enable during normal browsing — it can blank the live WebGL map.
     */
    reframeHomeStill?: boolean;
};

function blobFromCanvas(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((resolve, reject) => {
        try {
            canvas.toBlob((blob) => {
                if (blob) resolve(blob);
                else reject(new Error('Failed to export snapshot image'));
            }, 'image/png');
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            if (/taint/i.test(msg)) {
                reject(new Error('Tainted canvases may not be exported.'));
            } else {
                reject(err instanceof Error ? err : new Error(msg));
            }
        }
    });
}

export function isCanvasTaintError(err: unknown): boolean {
    const msg = err instanceof Error ? err.message : String(err || '');
    return /taint/i.test(msg);
}

/** Trim letterbox bars by comparing pixels to corner-sampled background. */
function contentBounds(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    opts: { step?: number } = {},
): { x: number; y: number; w: number; h: number } {
    const step = opts.step ?? 3;
    const { data } = ctx.getImageData(0, 0, w, h);
    const sample = (x: number, y: number) => {
        const i = (y * w + x) * 4;
        return [data[i], data[i + 1], data[i + 2]] as const;
    };
    const corners = [
        sample(2, 2),
        sample(w - 3, 2),
        sample(2, h - 3),
        sample(w - 3, h - 3),
    ];
    const bg = [
        (corners[0][0] + corners[1][0] + corners[2][0] + corners[3][0]) / 4,
        (corners[0][1] + corners[1][1] + corners[2][1] + corners[3][1]) / 4,
        (corners[0][2] + corners[1][2] + corners[2][2] + corners[3][2]) / 4,
    ];
    const delta = 26;
    let minX = w;
    let minY = h;
    let maxX = 0;
    let maxY = 0;
    let found = false;
    for (let y = 0; y < h; y += step) {
        for (let x = 0; x < w; x += step) {
            const i = (y * w + x) * 4;
            if (data[i + 3] < 12) continue;
            const dr = data[i] - bg[0];
            const dg = data[i + 1] - bg[1];
            const db = data[i + 2] - bg[2];
            if (dr * dr + dg * dg + db * db < delta * delta) continue;
            found = true;
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
        }
    }
    if (!found) return { x: 0, y: 0, w, h };
    // Side pad protects coasts; vertical pad stays tight.
    const padX = Math.round(w * 0.02);
    const padY = Math.round(h * 0.015);
    minX = Math.max(0, minX - padX);
    minY = Math.max(0, minY - padY);
    maxX = Math.min(w - 1, maxX + padX);
    maxY = Math.min(h - 1, maxY + padY);
    return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

/**
 * Captures the current map view and returns a branded PNG blob with the
 * caption rendered in a band below the map. Team logos are re-drawn over
 * the map so they survive the WebGL → 2D capture.
 */
export async function captureMapSnapshot(
    map: mapboxgl.Map,
    data: SnapshotCardData,
    logos: SnapshotLogo[] = [],
    options: CaptureMapSnapshotOptions = {},
): Promise<Blob> {
    await waitForMapIdle(map);

    const mapCanvas = map.getCanvas();
    const W = mapCanvas.width;
    const mapH = mapCanvas.height;
    const dpr = mapCanvas.clientWidth ? mapCanvas.width / mapCanvas.clientWidth : (window.devicePixelRatio || 1);
    const mapOnly = Boolean(options.mapOnly);
    const reframeHomeStill = Boolean(options.reframeHomeStill);

    // Homepage stills: paint current view (optionally reframed for seed), trim
    // letterbox bars, fit into 2× HOME_MAP_STILL size. Do not mutate the live
    // map unless reframeHomeStill is set — setProjection/fitBounds was blanking WebGL.
    if (mapOnly) {
        if (mapCanvas.clientWidth < 480 || mapCanvas.clientHeight < 280 || W < 480 || mapH < 280) {
            throw new Error('Map canvas too small for homepage still');
        }

        const prev = {
            center: map.getCenter(),
            zoom: map.getZoom(),
            bearing: map.getBearing(),
            pitch: map.getPitch(),
        };
        let prevProjection: string | null = null;
        try {
            if (reframeHomeStill) {
                try {
                    const proj = (map as mapboxgl.Map & { getProjection?: () => { name?: string } }).getProjection?.();
                    prevProjection = proj?.name || null;
                    (map as mapboxgl.Map & { setProjection: (p: string) => void }).setProjection('mercator');
                    const setFog = (map as mapboxgl.Map & { setFog?: (fog: null) => void }).setFog;
                    setFog?.call(map, null);
                    map.fitBounds(
                        [[-125.2, 24.3], [-66.5, 49.5]],
                        { padding: 28, duration: 0, essential: true, maxZoom: 4.5 },
                    );
                    await waitForMapIdle(map, { timeoutMs: 4000 });
                } catch {
                    /* keep current camera */
                }
            }

            const framedCanvas = map.getCanvas();
            const fW = framedCanvas.width;
            const fH = framedCanvas.height;
            const fDpr = framedCanvas.clientWidth
                ? framedCanvas.width / framedCanvas.clientWidth
                : dpr;

            const native = document.createElement('canvas');
            native.width = fW;
            native.height = fH;
            const nctx = native.getContext('2d');
            if (!nctx) throw new Error('Could not create snapshot canvas');
            nctx.drawImage(framedCanvas, 0, 0, fW, fH);
            const bounds = contentBounds(nctx, fW, fH);

            const out = document.createElement('canvas');
            out.width = HOME_MAP_STILL_W;
            out.height = HOME_MAP_STILL_H;
            const ctx = out.getContext('2d');
            if (!ctx) throw new Error('Could not create snapshot canvas');

            // Contain with a small margin so coast logos aren't clipped at the edge.
            const contain = Math.min(HOME_MAP_STILL_W / bounds.w, HOME_MAP_STILL_H / bounds.h);
            const fit = contain * 0.94;
            const dw = bounds.w * fit;
            const dh = bounds.h * fit;
            const dx = (HOME_MAP_STILL_W - dw) / 2;
            const dy = (HOME_MAP_STILL_H - dh) / 2;

            ctx.fillStyle = '#070a10';
            ctx.fillRect(0, 0, HOME_MAP_STILL_W, HOME_MAP_STILL_H);
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(native, bounds.x, bounds.y, bounds.w, bounds.h, dx, dy, dw, dh);

            const logoScale = 0.5;
            const loadedLogos = await Promise.all(
                logos.map(async (logo) => ({
                    logo,
                    img: logo.src ? await loadSnapshotImage(logo.src) : null,
                })),
            );
            for (const { logo, img } of loadedLogos) {
                const pt = map.project([logo.lng, logo.lat]);
                const s = logo.size * fDpr * logoScale * fit;
                const cx = dx + (pt.x * fDpr - bounds.x) * fit;
                const cy = dy + (pt.y * fDpr - bounds.y) * fit;
                if (cx < -s || cx > HOME_MAP_STILL_W + s || cy < -s || cy > HOME_MAP_STILL_H + s) continue;
                try {
                    if (img && img.naturalWidth > 0) {
                        drawSnapshotLogo(ctx, img, logo, cx, cy, s, fDpr);
                    } else if (logo.circular && logo.fallbackInitial) {
                        drawSnapshotFallbackAvatar(ctx, logo, cx, cy, s);
                    }
                } catch {
                    if (logo.circular && logo.fallbackInitial) {
                        drawSnapshotFallbackAvatar(ctx, logo, cx, cy, s);
                    }
                }
            }

            return await blobFromCanvas(out);
        } finally {
            if (reframeHomeStill) {
                try {
                    if (prevProjection && prevProjection !== 'mercator') {
                        (map as mapboxgl.Map & { setProjection: (p: string) => void }).setProjection(prevProjection);
                    }
                    map.jumpTo(prev);
                    map.triggerRepaint();
                } catch {
                    /* ignore restore errors */
                }
            }
        }
    }

    const out = document.createElement('canvas');
    const scale = W / 1200;
    const bandH = Math.round(150 * scale);
    out.width = W;
    out.height = mapH + bandH;
    const ctx = out.getContext('2d');
    if (!ctx) throw new Error('Could not create snapshot canvas');

    // 1. The live map (territories), top of the image.
    ctx.drawImage(mapCanvas, 0, 0, W, mapH);

    // 2. Team logos — re-projected onto the map. These are the ONLY things
    //    allowed to overlay the map. External avatars are proxied same-origin.
    const loadedLogos = await Promise.all(
        logos.map(async (logo) => ({
            logo,
            img: logo.src ? await loadSnapshotImage(logo.src) : null,
        })),
    );
    for (const { logo, img } of loadedLogos) {
        const pt = map.project([logo.lng, logo.lat]);
        const s = logo.size * dpr;
        const cx = pt.x * dpr;
        const cy = pt.y * dpr;
        if (cx < -s || cx > W + s || cy < -s || cy > mapH + s) continue;
        try {
            if (img && img.naturalWidth > 0) {
                drawSnapshotLogo(ctx, img, logo, cx, cy, s, dpr);
            } else if (logo.circular && logo.fallbackInitial) {
                drawSnapshotFallbackAvatar(ctx, logo, cx, cy, s);
            }
        } catch {
            if (logo.circular && logo.fallbackInitial) {
                drawSnapshotFallbackAvatar(ctx, logo, cx, cy, s);
            }
        }
    }

    paintSnapshotCaptionBand(ctx, W, mapH, data);
    return blobFromCanvas(out);
}

/**
 * Brand a pre-painted map canvas (own-map / non-Mapbox) with the same caption
 * band used by MapView snapshots. Logos should already be in the pixels.
 */
export async function composeSnapshotFromMapCanvas(
    mapCanvas: HTMLCanvasElement,
    data: SnapshotCardData,
): Promise<Blob> {
    const W = mapCanvas.width;
    const mapH = mapCanvas.height;
    if (W < 8 || mapH < 8) throw new Error('Map canvas too small for snapshot');

    const scale = W / 1200;
    const bandH = Math.round(150 * scale);
    const out = document.createElement('canvas');
    out.width = W;
    out.height = mapH + bandH;
    const ctx = out.getContext('2d');
    if (!ctx) throw new Error('Could not create snapshot canvas');

    ctx.drawImage(mapCanvas, 0, 0, W, mapH);
    paintSnapshotCaptionBand(ctx, W, mapH, data);
    return blobFromCanvas(out);
}

function paintSnapshotCaptionBand(
    ctx: CanvasRenderingContext2D,
    W: number,
    mapH: number,
    data: SnapshotCardData,
) {
    const scale = W / 1200;
    const bandH = Math.round(150 * scale);

    // Caption band BELOW the map — solid, never overlapping the map.
    ctx.fillStyle = BAND_BG;
    ctx.fillRect(0, mapH, W, bandH);
    ctx.fillStyle = 'rgba(255,255,255,0.08)';
    ctx.fillRect(0, mapH, W, Math.max(1, Math.round(scale)));

    const pad = 36 * scale;

    const shieldSize = 30 * scale;
    const brandY = mapH + 32 * scale;
    drawShield(ctx, pad, brandY, shieldSize);

    let cursorX = pad + shieldSize + 12 * scale;
    const brandTextY = brandY + shieldSize * 0.5;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = TEXT_PRIMARY;
    ctx.font = `900 ${22 * scale}px -apple-system, "Segoe UI", sans-serif`;
    const brandText = 'EMPIRE MAPS';
    ctx.fillText(brandText, cursorX, brandTextY);
    cursorX += ctx.measureText(brandText).width + 10 * scale;

    const badgeText = (data.badge || 'LIVE').toUpperCase();
    ctx.font = `800 ${11 * scale}px -apple-system, "Segoe UI", sans-serif`;
    const badgeTextW = ctx.measureText(badgeText).width;
    const badgePadX = 7 * scale;
    const badgeW = badgeTextW + badgePadX * 2;
    const badgeH = 18 * scale;
    ctx.fillStyle = 'rgba(249,115,22,0.15)';
    roundRect(ctx, cursorX, brandTextY - badgeH / 2, badgeW, badgeH, 4 * scale);
    ctx.fill();
    ctx.strokeStyle = 'rgba(249,115,22,0.45)';
    ctx.lineWidth = Math.max(1, scale);
    roundRect(ctx, cursorX, brandTextY - badgeH / 2, badgeW, badgeH, 4 * scale);
    ctx.stroke();
    ctx.fillStyle = BRAND_ORANGE;
    ctx.fillText(badgeText, cursorX + badgePadX, brandTextY + 0.5 * scale);
    cursorX += badgeW;

    const siteUrl = data.siteUrl || 'empiremaps.live';
    cursorX += 14 * scale;
    ctx.fillStyle = BRAND_ORANGE;
    ctx.font = `700 ${14 * scale}px -apple-system, "Segoe UI", sans-serif`;
    ctx.fillText(siteUrl, cursorX, brandTextY + 0.5 * scale);

    const titleY = brandY + shieldSize + 28 * scale;
    ctx.fillStyle = TEXT_PRIMARY;
    ctx.font = `800 ${26 * scale}px -apple-system, "Segoe UI", sans-serif`;
    ctx.fillText(data.title, pad, titleY);

    if (data.subtitle) {
        ctx.fillStyle = TEXT_SECONDARY;
        ctx.font = `500 ${15 * scale}px -apple-system, "Segoe UI", sans-serif`;
        ctx.fillText(data.subtitle, pad, titleY + 27 * scale);
    }

    if (data.leaderName) {
        const rightX = W - pad;
        ctx.textAlign = 'right';

        const label = (data.leaderLabel || 'Leader').toUpperCase();
        ctx.fillStyle = BRAND_ORANGE;
        ctx.font = `800 ${13 * scale}px -apple-system, "Segoe UI", sans-serif`;
        ctx.fillText(label, rightX, mapH + 40 * scale);

        ctx.fillStyle = TEXT_PRIMARY;
        ctx.font = `800 ${28 * scale}px -apple-system, "Segoe UI", sans-serif`;
        const nameY = mapH + 74 * scale;
        ctx.fillText(data.leaderName, rightX, nameY);

        if (data.leaderColor) {
            const nameW = ctx.measureText(data.leaderName).width;
            const sw = 18 * scale;
            ctx.fillStyle = data.leaderColor;
            roundRect(ctx, rightX - nameW - sw - 10 * scale, nameY - sw / 2, sw, sw, 4 * scale);
            ctx.fill();
        }

        if (data.leaderMeta) {
            ctx.fillStyle = TEXT_SECONDARY;
            ctx.font = `500 ${15 * scale}px -apple-system, "Segoe UI", sans-serif`;
            ctx.fillText(data.leaderMeta, rightX, mapH + 104 * scale);
        }
        ctx.textAlign = 'left';
    }
}

/** Builds the SnapshotLogo list from live Mapbox markers (logo elements). */
export function markersToLogos(markers: mapboxgl.Marker[]): SnapshotLogo[] {
    const logos: SnapshotLogo[] = [];
    for (const m of markers) {
        const el = m.getElement();
        const inner = el.querySelector('.fantasy-avatar-marker-inner') as HTMLElement | null;
        const img = el.querySelector('img') as HTMLImageElement | null;
        const circular = el.classList.contains('fantasy-avatar-marker');
        const rect = el.getBoundingClientRect();
        const size = rect.width || parseFloat(el.style.width) || 32;
        const ll = m.getLngLat();
        const src = (img?.currentSrc || img?.src || el.dataset.logoUrl || '').trim();
        const fallbackInitial = (
            inner?.textContent?.trim()
            || el.dataset.fallbackInitial
            || el.title?.split(' (')[0]
            || '?'
        ).slice(0, 1);

        if (!src && !circular) continue;
        if (!src && circular && !fallbackInitial) continue;

        logos.push({
            lng: ll.lng,
            lat: ll.lat,
            size,
            src,
            circular,
            ringColor: circular
                ? inner?.style.getPropertyValue('--ring') || el.dataset.ringColor || undefined
                : undefined,
            fallbackInitial: circular ? fallbackInitial : undefined,
        });
    }
    return logos;
}
